import { animate, state, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  forwardRef,
  inject,
  input,
  OnInit,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IsActiveMatchOptions, NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { Ripple } from 'primeng/ripple';
import { filter } from 'rxjs';
import { MenuService } from './app.menu.service';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[app-menuitem]',
  host: {
    '[class.layout-root-menuitem]': 'root()',
    '[class.active-menuitem]': 'active() && !root()'
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, RouterLinkActive, TranslateModule, Ripple, forwardRef(() => AppMenuitemComponent)],
  template: `
    <ng-container>
      @if (root() && item().visible !== false) {
        <div class="layout-menuitem-root-text">{{ item().label | translate }}</div>
      }
      @if ((!item().routerLink || item().items) && item().visible !== false) {
        <a
          [attr.href]="item().url"
          (click)="itemClick($event)"
          [class]="item().class"
          [attr.target]="item().target"
          tabindex="0"
          pRipple
        >
          @if (item().image) {
            <img class="novatiq-menuitem-image" [class]="item().label" [src]="item().image" [title]="item().label" />
          } @else {
            <i [class]="item().icon" class="layout-menuitem-icon"></i>
          }
          <span class="layout-menuitem-text mx-3">{{ item().label | translate }}</span>
          @if (item().beta) {
            <span class="beta-badge">Beta</span>
          }
          <span class="menuitem-selected"></span>
          @if (item().items) {
            <i class="pi pi-fw pi-angle-down layout-submenu-toggler"></i>
          }
        </a>
      }
      @if (item().routerLink && !item().items && item().visible !== false) {
        <a
          [id]="item().path"
          (click)="itemClick($event)"
          [class]="item().class"
          [routerLink]="item().routerLink"
          routerLinkActive="active-route"
          [routerLinkActiveOptions]="resolvedRouterLinkActiveOptions()"
          [fragment]="item().fragment"
          [queryParamsHandling]="item().queryParamsHandling"
          [preserveFragment]="item().preserveFragment"
          [skipLocationChange]="item().skipLocationChange"
          [replaceUrl]="item().replaceUrl"
          [state]="item().state"
          [queryParams]="item().queryParams"
          [attr.target]="item().target"
          tabindex="0"
          pRipple
        >
          @if (item().image) {
            <img
              class="novatiq-menuitem-image"
              [class]="item().label"
              [src]="item().image"
              [title]="item().label | translate"
            />
          } @else {
            <i [class]="item().icon" class="layout-menuitem-icon"></i>
          }
          <span class="layout-menuitem-text mx-3">{{ item().label | translate }}</span>
          @if (item().beta) {
            <span class="beta-badge">Beta</span>
          }
          <span class="menuitem-selected"></span>
          @if (item().items) {
            <i class="pi pi-fw pi-angle-down layout-submenu-toggler"></i>
          }
        </a>
      }
      @if (item().items && item().visible !== false) {
        <ul [@children]="submenuAnimation()">
          @for (child of item().items; track $index; let i = $index) {
            <li
              app-menuitem
              [item]="child"
              [index]="i"
              [parentKey]="menuKey()"
              class="menu-item"
              [class]="child.badgeClass"
            ></li>
          }
        </ul>
      }
    </ng-container>
  `,
  animations: [
    trigger('children', [
      state(
        'collapsed',
        style({
          height: '0'
        })
      ),
      state(
        'expanded',
        style({
          height: '*'
        })
      ),
      transition('collapsed <=> expanded', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)'))
    ])
  ]
})
export class AppMenuitemComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly menuService = inject(MenuService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly defaultRouterLinkActiveOptions: IsActiveMatchOptions = {
    paths: 'exact',
    queryParams: 'ignored',
    matrixParams: 'ignored',
    fragment: 'ignored'
  };

  readonly item = input.required<MenuItem>();
  readonly index = input.required<number>();
  readonly root = input(false);
  readonly parentKey = input<string | undefined>(undefined);

  readonly active = signal(false);

  readonly menuKey = computed(() => {
    const parentKey = this.parentKey();
    const index = this.index();
    return parentKey ? `${parentKey}-${index}` : String(index);
  });

  readonly resolvedRouterLinkActiveOptions = computed(
    (): IsActiveMatchOptions => this.item().routerLinkActiveOptions ?? this.defaultRouterLinkActiveOptions
  );

  readonly submenuAnimation = computed(() => (this.root() ? 'expanded' : this.active() ? 'expanded' : 'collapsed'));

  constructor() {
    this.menuService.menuSource$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      // Defer until required inputs (e.g. index) are bound on child menu items.
      queueMicrotask(() => {
        const key = this.menuKey();
        if (value.routeEvent) {
          this.active.set(value.key === key || value.key.startsWith(key + '-'));
        } else if (value.key !== key && !value.key.startsWith(key + '-')) {
          this.active.set(false);
        }
      });
    });

    this.menuService.resetSource$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.active.set(false);
    });

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if (this.item().routerLink) {
          this.updateActiveStateFromRoute();
        }
      });
  }

  ngOnInit(): void {
    if (this.item().routerLink) {
      this.updateActiveStateFromRoute();
    }
  }

  updateActiveStateFromRoute(): void {
    const routerLink = this.item().routerLink;
    if (!routerLink?.length) {
      return;
    }

    const activeRoute = this.router.isActive(routerLink[0], this.resolvedRouterLinkActiveOptions());
    if (activeRoute) {
      this.menuService.onMenuStateChange({ key: this.menuKey(), routeEvent: true });
    }
  }

  itemClick(event: Event): void {
    const menuItem = this.item();
    if (menuItem.disabled) {
      event.preventDefault();
      return;
    }

    if (menuItem.command) {
      menuItem.command({ originalEvent: event, item: menuItem });
    }

    if (menuItem.items) {
      this.active.update((value) => !value);
    }

    this.menuService.onMenuStateChange({ key: this.menuKey() });
  }
}
